import { clearTokens, loadTokens, saveTokens } from '../auth/tokenStore';
import { t } from '@/lib/i18n';
import { ApiError, type ApiErrorBody, NetworkError } from './errors';
import type { Tokens } from './types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Called when the session is unrecoverable, so the app can route back to /entrar. */
let onSessionLost: ((reason: 'expired' | 'reuse') => void) | null = null;

export function setSessionLostHandler(fn: (reason: 'expired' | 'reuse') => void): void {
  onSessionLost = fn;
}

// --------------------------------------------------------------- refresh

/**
 * Refresh coordination.
 *
 * The backend rotates the refresh token on every use and treats a second use of a rotated
 * token as **reuse**, which revokes the entire token family — every device signed out.
 * Verified against the running API: two sequential refreshes with the same token give
 * `401 Refresh token reuse detected`, and the token issued by the *first* one is dead too.
 *
 * So refreshes must be serialised, and a per-module promise is not enough. Two browser tabs
 * are two JS contexts sharing one localStorage; each would run its own "single flight" and
 * the second would trip reuse detection. Two guards handle that:
 *
 *   1. `navigator.locks` serialises across tabs of the same origin.
 *   2. Inside the lock, re-read storage first — if another tab already rotated the token
 *      while we waited, adopt its result instead of spending ours.
 *
 * Guard 2 is what actually prevents the revocation; the lock just makes it reliable.
 */
const REFRESH_LOCK = 'mrf.refresh';

let refreshInFlight: Promise<Tokens> | null = null;

async function doRefresh(tokenAtEntry: string): Promise<Tokens> {
  // Someone else may have refreshed while we waited for the lock.
  const latest = loadTokens();
  if (latest && latest.refreshToken !== tokenAtEntry) return latest;
  if (!latest) throw new ApiError(unauthorizedBody(t('errors.noSession')));

  try {
    const tokens = await rawRequest<Tokens>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken: latest.refreshToken },
      auth: false,
    });
    saveTokens(tokens);
    return tokens;
  } catch (err) {
    /**
     * Only a 401 means the session is actually gone. A 429, a 5xx or a dropped connection
     * says nothing about whether the refresh token is still valid — and destroying it there
     * turns a transient hiccup into a forced logout. That is not hypothetical: booting
     * several contexts at once puts enough load on the 10 req/min `/auth/*` bucket to
     * produce a 429, and the old code signed the user out over it.
     */
    if (err instanceof ApiError && err.isUnauthorized) {
      clearTokens();
      onSessionLost?.(err.isTokenReuse ? 'reuse' : 'expired');
    }
    throw err;
  }
}

async function refreshSession(): Promise<Tokens> {
  refreshInFlight ??= (async () => {
    try {
      const current = loadTokens();
      if (!current) throw new ApiError(unauthorizedBody(t('errors.noSession')));
      const tokenAtEntry = current.refreshToken;

      // Web Locks needs a secure context; localhost qualifies. Fall back to the in-tab
      // guard where it is missing rather than failing the request outright.
      if (typeof navigator !== 'undefined' && navigator.locks) {
        return await navigator.locks.request(REFRESH_LOCK, () => doRefresh(tokenAtEntry));
      }
      return await doRefresh(tokenAtEntry);
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function unauthorizedBody(message: string): ApiErrorBody {
  return {
    statusCode: 401,
    code: 'UNAUTHORIZED',
    message,
    timestamp: new Date().toISOString(),
    path: '',
  };
}

// --------------------------------------------------------------- request

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * Serialised as JSON. The backend runs ValidationPipe with `forbidNonWhitelisted`,
   * so any property outside the DTO is a hard 400 — never spread a response object back
   * into a request body.
   */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  auth?: boolean;
  signal?: AbortSignal;
}

async function rawRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, signal } = opts;

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const tokens = loadTokens();
    if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError(err);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    throw new ApiError(asErrorBody(payload, res.status, url.pathname), readRetryAfter(res.headers));
  }

  return payload as T;
}

/**
 * Seconds to wait after a 429.
 *
 * @nestjs/throttler v6 suffixes the header with the throttler's name when the throttler is
 * a *named* one — our auth bucket answers with `Retry-After-auth`, not `Retry-After`. Read
 * the plain header first, then fall back to scanning for any suffixed variant, so this keeps
 * working if the bucket is ever renamed.
 */
function readRetryAfter(headers: Headers): number | null {
  const candidates: string[] = [];
  const plain = headers.get('Retry-After');
  if (plain) candidates.push(plain);

  headers.forEach((value, name) => {
    if (name.toLowerCase().startsWith('retry-after-')) candidates.push(value);
  });

  for (const raw of candidates) {
    const seconds = Number(raw);
    if (Number.isFinite(seconds) && seconds > 0) return Math.ceil(seconds);
  }
  return null;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

/** Coerces anything the server returned into the documented error envelope. */
function asErrorBody(payload: unknown, status: number, path: string): ApiErrorBody {
  if (
    payload &&
    typeof payload === 'object' &&
    'message' in payload &&
    typeof (payload as ApiErrorBody).message === 'string'
  ) {
    const body = payload as ApiErrorBody;
    return {
      statusCode: body.statusCode ?? status,
      code: body.code ?? `HTTP_${status}`,
      message: body.message,
      timestamp: body.timestamp ?? new Date().toISOString(),
      path: body.path ?? path,
      details: body.details,
    };
  }
  return {
    statusCode: status,
    code: `HTTP_${status}`,
    message: 'Algo deu errado. Tente de novo.',
    timestamp: new Date().toISOString(),
    path,
  };
}

/**
 * The public entry point: same as rawRequest, but a 401 on an authenticated call
 * transparently refreshes once and replays the original request.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const auth = opts.auth ?? true;

  try {
    return await rawRequest<T>(path, opts);
  } catch (err) {
    const retriable = auth && err instanceof ApiError && err.isUnauthorized;
    if (!retriable) throw err;

    await refreshSession(); // throws if the session is truly gone
    return rawRequest<T>(path, opts);
  }
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};
